/** Claims — list view (server-side pagination/sort/search + delete). */
import * as React from "react";
import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useTable } from "@refinedev/react-table";
import {
  useGo,
  useNavigation,
  useDelete,
  type HttpError,
} from "@refinedev/core";
import { Icon } from "@/components/ui/icon";
import type { Claim } from "@aos/shared";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/app/StatusBadge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ConfirmDialog } from "@/components/app/ConfirmDialog";

import {
  DataTable,
  PageHeader,
  SearchBox,
  useSearchFilter,
  type RefineTableCore,
} from "../projects/_shared";
import { humanize } from "../projects/_form";

export function ClaimList() {
  const go = useGo();
  const { editUrl, showUrl, createUrl } = useNavigation();
  const { mutate: deleteClaim } = useDelete();
  const [pendingDelete, setPendingDelete] = React.useState<Claim | null>(null);

  const columns = useMemo<ColumnDef<Claim, unknown>[]>(
    () => [
      {
        id: "title",
        accessorKey: "title",
        header: "Title",
        cell: ({ row }) => (
          <span className="font-medium">{row.original.title}</span>
        ),
      },
      {
        id: "claim_type",
        accessorKey: "claim_type",
        header: "Type",
        cell: ({ row }) => (
          <Badge variant="secondary">{humanize(row.original.claim_type)}</Badge>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "confidence_score",
        accessorKey: "confidence_score",
        header: "Confidence",
        cell: ({ row }) =>
          row.original.confidence_score != null ? (
            <span className="font-mono text-xs tabular-nums">
              {row.original.confidence_score.toFixed(2)}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {new Date(row.original.created_at).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions
            onEdit={() => go({ to: editUrl("claims", row.original.id) })}
            onShow={() => go({ to: showUrl("claims", row.original.id) })}
            onDelete={() => setPendingDelete(row.original)}
          />
        ),
      },
    ],
    [go, editUrl, showUrl],
  );

  const table = useTable<Claim, HttpError>({
    columns,
    refineCoreProps: {
      resource: "claims",
      pagination: { pageSize: 25 },
      sorters: { initial: [{ field: "created_at", order: "desc" }] },
    },
  });

  const core = table.refineCore as unknown as RefineTableCore & {
    setFilters: (
      filters: { field: string; operator: "contains"; value: string }[],
    ) => void;
  };
  const [search, setSearch] = useSearchFilter(core.setFilters);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Claims"
        description="Decisions, transitions, and assertions evidence is gathered for or against."
        action={
          <Button onClick={() => go({ to: createUrl("claims") })}>
            <Icon name="create" size={16} className="mr-2" aria-hidden /> New
            Claim
          </Button>
        }
      />

      <SearchBox
        value={search}
        onChange={setSearch}
        placeholder="Search claims…"
      />

      <DataTable
        table={table}
        core={core}
        columns={columns}
        emptyMessage="No claims recorded yet."
        onRowClick={(c) => go({ to: showUrl("claims", c.id) })}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open: boolean) => !open && setPendingDelete(null)}
        title="Delete claim?"
        description={
          <>
            This removes{" "}
            <span className="font-medium">{pendingDelete?.title}</span>. This
            action cannot be undone.
          </>
        }
        confirmLabel="Delete"
        onConfirm={() => {
          if (pendingDelete) {
            deleteClaim({ resource: "claims", id: pendingDelete.id });
          }
        }}
      />
    </div>
  );
}

function RowActions({
  onEdit,
  onShow,
  onDelete,
}: {
  onEdit: () => void;
  onShow: () => void;
  onDelete: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Icon name="more" size={16} aria-hidden />
          <span className="sr-only">Open actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onShow}>View</DropdownMenuItem>
        <DropdownMenuItem onClick={onEdit}>
          <Icon name="edit" size={16} className="mr-2" aria-hidden /> Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={onDelete}
        >
          <Icon name="delete" size={16} className="mr-2" aria-hidden /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ClaimList;
